import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getVendorActivities, logVendorActivity } from "services/activity";
import { loggedInUserState } from "redux/userSlice";

export const EMAIL_ACTIVITIES = {
  EMAIL_SEND: "EMAIL_SEND",
};
export const CERTIFICATE_ACTIVITIES = {
  CERTIFICATE_CREATION: "CERTIFICATE_CREATION",
  REQUEST_CERTIFICATE: "REQUEST_CERTIFICATE",
  DELETE_CERTIFICATE: "DELETE_CERTIFICATE",
};

const fieldsToCompare = ["name", "entityName", "email", "additionalEmails", "phone", "ein", "address1", "address2", "city", "state", "zip", "status", "notificationStatus"];
const emailActivities = [
  EMAIL_ACTIVITIES.EMAIL_SEND,
];
const certificateActivities = [
  CERTIFICATE_ACTIVITIES.CERTIFICATE_CREATION,
  CERTIFICATE_ACTIVITIES.REQUEST_CERTIFICATE,
  CERTIFICATE_ACTIVITIES.DELETE_CERTIFICATE,
];

export default function useVendorActivities (clientVendor) {
  const [originalData, setOriginalData] = useState({});
  const [vendorActivities, setVendorActivities] = useState([]);
  const { loggedInUser } = useSelector(loggedInUserState);
  let userFullName = loggedInUser?.email;
  if (loggedInUser?.firstName || loggedInUser?.lastName) {
    userFullName = `${loggedInUser.firstName}${loggedInUser.firstName ? " " : ""}${loggedInUser.lastName}`;
  }

  const loadVendorActivities = async function (clientVendorID) {
    const targetID = clientVendor?.id || clientVendorID;
    if (!targetID) {
      console.warn('No clientVendorID provided to loadVendorActivities');
      return;
    }
    const result = await getVendorActivities(targetID);
    setVendorActivities(result);
  };

  const addVendorActivity = async function ({
    vendor = {},
    categoryUpdateResult,
    categories,
    projectsUpdateResult,
    projects,
    action = "EDIT",
    clientVendorID,
  }) {
    if (action === "CREATE" && clientVendorID) {
      await logVendorActivity({
        clientVendorID,
        userFullName,
        userID: loggedInUser.id,
        action: "CREATE",
      });
    } else if (emailActivities.includes(action) && clientVendorID) {
      await logVendorActivity({
        clientVendorID,
        userFullName,
        userID: loggedInUser.id,
        action,
        field: JSON.stringify({ message: vendor.message, details: vendor.details }),
      });
    } else if (certificateActivities.includes(action) && clientVendorID) {
      await logVendorActivity({
        clientVendorID,
        userFullName,
        userID: loggedInUser.id,
        action,
        field: JSON.stringify({ message: vendor.message, details: vendor.details }),
      });
    } else {
      const originalVendorInfo =
        {
          ...originalData?.vendor,
          ...(vendor?.status && { status: originalData?.status }),
          ...(vendor?.notificationStatus && { notificationStatus: originalData?.notificationStatus }),
        } || {};
      const updates = {};
      fieldsToCompare.forEach((key) => {
        if (
          key === "additionalEmails" &&
          ((vendor[key] && vendor[key].length) || (originalVendorInfo[key] && originalVendorInfo[key].length)) &&
          vendor[key]?.toString() !== originalVendorInfo[key]?.toString()
        ) {
          updates[key] = { prev: originalVendorInfo[key], next: vendor[key] };
        } else if (String(originalVendorInfo[key]) !== String(vendor[key]) && key !== "additionalEmails") {
          updates[key] = { prev: originalVendorInfo[key], next: vendor[key] };
        }
      });
      if (categoryUpdateResult && categories?.length) {
        const { categoriesAdded = [], categoriesDeleted = [] } = categoryUpdateResult;
        categoriesAdded.forEach((categoryId) => {
          const categoryAdded = categories.find((clientCategory) => categoryId === clientCategory.id);
          if (categoryAdded) {
            updates["Category - " + categoryAdded.name] = { prev: "action", next: "added" };
          }
        });
        categoriesDeleted.forEach((categoryId) => {
          const categoryDeleted = categories.find((clientCategory) => categoryId === clientCategory.id);
          if (categoryDeleted) {
            updates["Category - " + categoryDeleted.name] = { prev: "action", next: "deleted" };
          }
        });
      }
      if (projectsUpdateResult && projects?.length) {
        const { projectsAdded = [], projectsDeleted = [] } = projectsUpdateResult;
        projectsAdded?.forEach((projectId) => {
          const projectAdded = projects.find((clientProject) => projectId === clientProject.id);
          if (projectAdded) {
            updates["Project - " + projectAdded.name] = { prev: "action", next: "added" };
          }
        });
        projectsDeleted?.forEach((projectId) => {
          const projectDeleted = projects.find((clientProject) => projectId === clientProject.id);
          if (projectDeleted) {
            updates["Project - " + projectDeleted.name] = { prev: "action", next: "deleted" };
          }
        });
      }
      if (Object.keys(updates).length) {
        await logVendorActivity({
          clientVendorID: clientVendor.id,
          userFullName,
          userID: loggedInUser.id,
          action: "EDIT",
          field: JSON.stringify(updates),
        });
      }
    }
    await loadVendorActivities();
  };

  useEffect(() => {
    if (clientVendor?.id) {
      setOriginalData(clientVendor);
      loadVendorActivities();
    }
  }, [clientVendor]);

  return [vendorActivities, addVendorActivity];
}
